import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Trash2, FolderOpen } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { TiltCard } from "@/components/common/TiltCard";
import { SkeletonCard } from "@/components/common/Skeleton";
import { StatusBadge } from "@/components/common/Badges";
import { staggerContainer, staggerItem } from "@/lib/motion";
import { TID } from "@/lib/testIds";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Workspaces() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/workspaces");
            setItems(data);
        } catch (e) {
            console.warn("Failed to load workspaces", e);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!name.trim()) return;
        setCreating(true);
        try {
            const { data } = await api.post("/workspaces", { name: name.trim(), description: desc, tags: [] });
            toast.success("Workspace created");
            setOpen(false);
            setName(""); setDesc("");
            nav(`/workspaces/${data.id}`);
        } catch (e) {
            toast.error("Failed to create");
        } finally {
            setCreating(false);
        }
    };

    const remove = async (id, ev) => {
        ev.stopPropagation();
        if (!confirm("Delete this workspace and all its analysis?")) return;
        try {
            await api.delete(`/workspaces/${id}`);
            toast.success("Workspace deleted");
            load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const filtered = items.filter((w) => (w.name || "").toLowerCase().includes(q.toLowerCase()));

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
                <div className="flex items-center gap-3 panel px-3 py-2 md:max-w-md w-full">
                    <Search size={14} className="text-slate-500" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search workspaces…"
                        className="bg-transparent outline-none flex-1 text-sm text-slate-200 placeholder:text-slate-500"
                    />
                </div>

                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <button data-testid={TID.newWorkspaceBtn} className="btn-primary flex items-center gap-2">
                            <Plus size={14} /> New Workspace
                        </button>
                    </DialogTrigger>
                    <DialogContent className="bg-[var(--bg-elevated)] border border-cyan-500/20 text-slate-200">
                        <DialogHeader>
                            <DialogTitle className="font-display text-white">Create Engineering Workspace</DialogTitle>
                            <DialogDescription className="text-xs text-slate-500 font-mono">
                                A workspace holds the uploaded SAP artifacts, findings and recommendations for one engagement.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-2">
                            <div>
                                <label className="label-eyebrow block mb-2">Name</label>
                                <input
                                    data-testid={TID.workspaceNameInput}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. S/4HANA Phase-2 Authorization Review"
                                    className="field"
                                />
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-2">Description</label>
                                <textarea
                                    data-testid={TID.workspaceDescInput}
                                    value={desc}
                                    onChange={(e) => setDesc(e.target.value)}
                                    rows={3}
                                    placeholder="Scope, in-scope systems, audit context…"
                                    className="field"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <button
                                data-testid={TID.workspaceCreateSubmit}
                                onClick={create}
                                disabled={creating}
                                className="btn-primary"
                            >
                                {creating ? "Creating…" : "Create Workspace"}
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <SkeletonCard key={i} testId={`workspace-skeleton-${i}`} />
                    ))}
                </div>
            ) : (
            <motion.div
                data-testid={TID.workspaceList}
                variants={staggerContainer}
                initial="initial"
                animate="animate"
                className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
            >
                {filtered.map((w) => (
                    <motion.div key={w.id} variants={staggerItem}>
                        <TiltCard
                            testId={TID.workspaceRow(w.id)}
                            max={6}
                            role="button"
                            tabIndex={0}
                            onClick={() => nav(`/workspaces/${w.id}`)}
                            onKeyDown={(e) => { if (e.key === "Enter") nav(`/workspaces/${w.id}`); }}
                            className="group panel-elevated card-lift text-left p-5 relative overflow-hidden cursor-pointer rounded-[4px]"
                        >
                            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-cyan-500/5 blur-2xl pointer-events-none group-hover:bg-cyan-500/10 transition-colors duration-500" />
                            <div className="flex items-start justify-between" style={{ transform: "translateZ(30px)" }}>
                                <div className="flex items-center gap-2">
                                    <FolderOpen size={14} className="text-cyan-400" />
                                    <StatusBadge status={w.status} />
                                </div>
                                <button
                                    onClick={(e) => remove(w.id, e)}
                                    className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-400 transition-opacity"
                                    aria-label="Delete"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <h3 className="font-display text-lg text-white mt-3 line-clamp-2" style={{ transform: "translateZ(40px)" }}>{w.name}</h3>
                            <p className="text-xs text-slate-500 line-clamp-2 mt-1 min-h-[2rem]">{w.description || "—"}</p>

                            <div className="grid grid-cols-3 gap-2 mt-4" style={{ transform: "translateZ(24px)" }}>
                                <div className="text-center panel px-2 py-2">
                                    <div className="font-display text-lg text-cyan-300">{w.artifact_count}</div>
                                    <div className="text-[9px] font-mono uppercase text-slate-500">Artifacts</div>
                                </div>
                                <div className="text-center panel px-2 py-2">
                                    <div className="font-display text-lg text-amber-400">{w.finding_count}</div>
                                    <div className="text-[9px] font-mono uppercase text-slate-500">Findings</div>
                                </div>
                                <div className="text-center panel px-2 py-2">
                                    <div className="font-display text-lg text-rose-400">{w.risk_score?.toFixed?.(0) ?? 0}</div>
                                    <div className="text-[9px] font-mono uppercase text-slate-500">Risk</div>
                                </div>
                            </div>

                            <div className="mt-4 text-[10px] font-mono uppercase tracking-wider text-slate-500">
                                Created {new Date(w.created_at).toLocaleDateString()}
                            </div>
                        </TiltCard>
                    </motion.div>
                ))}
                {filtered.length === 0 && (
                    <Panel className="md:col-span-2 xl:col-span-3" eyebrow="Empty" title="No workspaces yet">
                        <p className="text-sm text-slate-400">
                            Create your first workspace to start uploading SAP engineering artifacts and run offline inspections.
                        </p>
                    </Panel>
                )}
            </motion.div>
            )}
        </div>
    );
}
